// formGroupsCache — one network request per distinct form-groups query.
//
// WHY
// A single page routinely asks for the SAME form-groups payload several times
// over, because the parts that need it are independent of each other and none
// of them can know the others exist: on Quick Submit the job header card, the
// form's cross-module prefill and a job-source lookup each fetch the same job,
// and the submission config is fetched once unscoped and again client-scoped.
// Those payloads are large (60 kB for a submission config), so the duplicates
// are the expensive kind — a measured 245-request, ~4.8 s load with several
// identical 60 kB and 15 kB responses in it.
//
// The honest fix is not to make each caller aware of the others — that couples
// components that have no business knowing about each other, and breaks the
// moment a new one is added. It is to make the REQUEST idempotent: identical
// queries fired close together share one response.
//
// TWO MECHANISMS, deliberately:
//
//   • in-flight coalescing — a query that is already running is joined rather
//     than reissued. Always safe: the answer cannot be staler than the request
//     that is still in the air.
//   • a SHORT time-to-live — covers the common case where the duplicates are
//     sequential rather than overlapping (a child mounts after its parent's
//     fetch has already resolved). This is the part that can serve stale data,
//     so it is kept to a few seconds and any write invalidates the whole cache.
//
// Errors are never cached: a failed fetch removes its entry so the next caller
// retries for real instead of inheriting the failure.
//
// Nothing here knows a module or a field. The cache key is the request URL, so
// scopes that differ in any way (id, clientId, view, group, action) are
// different entries and can never be served each other's data.

// Long enough to cover one page's mount burst, short enough that no realistic
// user action fits inside it. Writes invalidate regardless — see below.
const DEFAULT_TTL_MS = 4000;

const entries = new Map(); // url -> { at, promise }

let ttlMs = DEFAULT_TTL_MS;

/** Test/host hook: change the window, or set 0 to keep only in-flight coalescing. */
export function setFormGroupsCacheTtl(ms) {
  ttlMs = Math.max(0, Number(ms) || 0);
}

/**
 * Drop everything. Called after any module write: a save is exactly the moment
 * a cached payload stops describing the record, and a stale read here would
 * show the user their own change missing.
 */
export function invalidateFormGroupsCache() {
  entries.clear();
}

/**
 * fetchFormGroupsCached — run `loader()` for `url`, or join/replay a recent
 * identical run.
 *
 * `loader` returns the RAW payload, so every caller shares one request no
 * matter which shape it goes on to read out of it (the groups array, or the
 * whole envelope with recordMeta).
 */
export function fetchFormGroupsCached(url, loader) {
  const now = Date.now();
  const hit = entries.get(url);
  // A pending entry is always joinable; a settled one only within the window.
  if (hit && (hit.pending || now - hit.at < ttlMs)) return hit.promise;

  const entry = { at: now, pending: true };
  entry.promise = Promise.resolve()
    .then(loader)
    .then((value) => {
      entry.pending = false;
      // Stamp on COMPLETION, not on start: a slow request must not begin life
      // already half-expired, or a burst behind it would reissue immediately.
      entry.at = Date.now();
      return value;
    })
    .catch((err) => {
      // Never cache a failure — the next caller must get a real attempt.
      if (entries.get(url) === entry) entries.delete(url);
      throw err;
    });
  entries.set(url, entry);
  return entry.promise;
}

export default { fetchFormGroupsCached, invalidateFormGroupsCache, setFormGroupsCacheTtl };
